import React, { useState } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Collapse,
  Typography,
  Box,
  IconButton,
  Badge,
  Paper,
  LinearProgress
} from '@mui/material';
import { ExpandLess, ExpandMore, CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';


const checklistData = [
    {
      category: "Structural Integrity",
      levels: {
        primary: ["Foundation Issues", "Roofing Problems", "Wall Cracks"],
        secondary: ["Hidden Foundation Damage", "Compromised Load-Bearing Walls", "Roof Decking Rot"],
        tertiary: ["Full Building Reengineering", "Structural Overload", "Construction Delays"],
        quaternary: ["Foundation Repair Complexity", "Load Redistribution Failures", "Structural Overload Consequences"]
      }
    },
    {
      category: "Electrical System",
      levels: {
        primary: ["Outdated Wiring", "Overloaded Circuits", "Non-compliant Electrical Panels"],
        secondary: ["Wiring Behind Walls", "Inadequate Electrical Capacity", "Fire Hazards"],
        tertiary: ["Full Electrical Rewiring", "Service Panel Upgrade", "Fire Code Upgrades"],
        quaternary: ["Grid Connectivity Issues", "Electromagnetic Interference", "System Overload"]
      }
    },
    {
      category: "Plumbing Issues",
      levels: {
        primary: ["Leaky Pipes", "Low Water Pressure", "Outdated Plumbing"],
        secondary: ["Corroded Pipes", "Leaking or Damaged Sewer Lines", "Water Damage Behind Walls"],
        tertiary: ["Systemic Plumbing Failure", "Sewer Line Replacement", "Mold Remediation"],
        quaternary: ["Major Excavation", "Water Contamination", "Regulatory Scrutiny"]
      }
    },
    {
      category: "HVAC System",
      levels: {
        primary: ["Inadequate Ventilation", "Old HVAC Equipment", "Temperature Inconsistencies"],
        secondary: ["Ductwork Problems", "Mold in Vents", "Inadequate Insulation"],
        tertiary: ["Building-Wide Airflow Redesign", "Comprehensive Mold Removal", "Energy Code Non-compliance"],
        quaternary: ["Redesigning HVAC Ductwork", "Infiltration of Pollutants", "Building Envelope Improvements"]
      }
    },
    {
      category: "Water Damage",
      levels: {
        primary: ["Signs of Moisture", "Mold Growth", "Flood Risk"],
        secondary: ["Structural Rot", "Mold Infestation", "Compromised Insulation"],
        tertiary: ["Building-Wide Mold Contamination", "Compromised Structural Integrity", "Insurance Complications"],
        quaternary: ["Widespread Mold Contamination", "Full Building Reengineering", "Structural Reinforcement Issues"]
      }
    },
    {
      category: "Fire Safety",
      levels: {
        primary: ["Non-compliant Fire Alarms", "Insufficient Sprinkler System", "Blocked or Inadequate Exits"],
        secondary: ["Outdated Fire Suppression Systems", "Fire Code Violations", "Compromised Firewalls"],
        tertiary: ["Complete System Overhaul", "Occupancy Restrictions", "Legal Action"],
        quaternary: ["Building-Wide Fireproofing", "Fire Suppression System Failure", "Regulatory Enforcement"]
      }
    },
    {
      category: "Building Code Compliance",
      levels: {
        primary: ["ADA Compliance", "Signage", "Zoning Issues"],
        secondary: ["Unpermitted Previous Work", "Non-Compliance with New Codes", "Zoning Variances"],
        tertiary: ["Major Redesigns", "Legal Compliance", "Litigation Risks"],
        quaternary: ["Re-zoning Requirements", "Legal Action and Fines", "Major Redesign Costs"]
      }
    },
    {
      category: "Exterior Concerns",
      levels: {
        primary: ["Façade Condition", "Parking Lot Issues", "Window and Door Seals"],
        secondary: ["Hidden Façade Damage", "Pest Damage", "Underground Utilities"],
        tertiary: ["Façade Re-engineering", "Pest-Induced Structural Damage", "Utility Coordination"],
        quaternary: ["Façade Collapse", "Pest Damage Spread", "Civil Engineering Challenges"]
      }
    },
    {
      category: "Environmental Hazards",
      levels: {
        primary: ["Asbestos", "Lead Paint", "Pest Infestation"],
        secondary: ["Widespread Asbestos", "Lead Contamination", "Pest Infestation"],
        tertiary: ["Asbestos Containment", "Lead Abatement", "Pest Control Protocols"],
        quaternary: ["Widespread Environmental Contamination", "Government Intervention", "Long-Term Health Risks"]
      }
    },
    {
      category: "Utility Connections",
      levels: {
        primary: ["Water and Gas Lines", "Sewer and Drainage"],
        secondary: ["Aging Infrastructure", "Undocumented Utility Lines", "Water Pressure Issues"],
        tertiary: ["Utility Infrastructure Upgrades", "Permitting Delays", "Re-piping Needs"],
        quaternary: ["Utility Infrastructure Collapse", "Extended Service Disruptions", "Permitting and Regulatory Delays"]
      }
    },
    {
      category: "Energy Efficiency",
      levels: {
        primary: ["Insulation", "Old Windows", "Lighting"],
        secondary: ["Poorly Insulated Walls", "Drafty Windows and Doors", "Inefficient HVAC Ducts"],
        tertiary: ["Building-Wide Energy Retrofit", "Unforeseen Environmental Impact", "Increased Operating Costs"],
        quaternary: ["Complete Insulation Replacement", "Window and Door Replacement", "Building-Wide Retrofit Challenges"]
      }
    },
    {
      category: "Aesthetic Issues",
      levels: {
        primary: ["Worn-Out Flooring", "Peeling Paint", "Outdated Fixtures"],
        secondary: ["Hidden Flooring Damage", "Extensive Wall Repairs", "Inconsistent Design Elements"],
        tertiary: ["Structural Flooring Repairs", "Extensive Drywall Replacement", "Comprehensive Design Overhaul"],
        quaternary: ["Structural Design Conflicts", "Material Shortages", "Occupant Dissatisfaction"]
      }
    },
    {
      category: "Health and Safety",
      levels: {
        primary: ["Indoor Air Quality", "Slip and Trip Hazards", "Hidden Safety Violations"],
        secondary: ["Airborne Contaminants", "Trip Hazards", "Hidden Safety Violations"],
        tertiary: ["Air Quality Remediation", "Safety System Upgrades", "Building-Wide Safety Audits"],
        quaternary: ["Chronic Health Issues", "Comprehensive Safety Retrofit", "Regulatory Enforcement Actions"]
      }
    }
  ];
  
  const initialCheckedItems = {
    "Structural Integrity-primary-Foundation Issues": true,
    "Electrical System-primary-Outdated Wiring": true,
    "Plumbing Issues-secondary-Leaking or Damaged Sewer Lines": true,
    "HVAC System-primary-Old HVAC Equipment": true,
    "Fire Safety-secondary-Fire Code Violations": true,
  };
  
  const InspectionChecklist = () => {
    const [checkedItems, setCheckedItems] = useState(initialCheckedItems);
    const [openCategories, setOpenCategories] = useState({});
    const [isChecklistOpen, setIsChecklistOpen] = useState(true);
  
    const handleToggle = (category, level, issue) => {
      const categoryKey = `${category}-${level}-${issue}`;
      setCheckedItems({
        ...checkedItems,
        [categoryKey]: !checkedItems[categoryKey],
      });
    };
  
    const handleCategoryToggle = (category) => {
      setOpenCategories({
        ...openCategories,
        [category]: !openCategories[category],
      });
    };
  
    const getCategoryProgress = (category) => {
      const totalIssues = Object.values(category.levels).flat().length;
      const checkedIssues = Object.keys(checkedItems).filter((key) =>
        key.startsWith(category.category) && checkedItems[key]
      ).length;
      const progress = (checkedIssues / totalIssues) * 100;
      let statusIcon = <RadioButtonUnchecked color="error" />;
  
      if (progress === 100) statusIcon = <CheckCircle color="success" />;
      else if (progress > 0) statusIcon = <RadioButtonUnchecked color="warning" />;
  
      return { progress, statusIcon };
    };
  
    return (
      <Paper elevation={3} sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" color="text.primary">
            Inspection Checklist
          </Typography>
          <IconButton onClick={() => setIsChecklistOpen(!isChecklistOpen)}>
            {isChecklistOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        <Collapse in={isChecklistOpen} timeout="auto" unmountOnExit>
          <List>
            {checklistData.map((category, index) => {
              const { progress, statusIcon } = getCategoryProgress(category);
              return (
                <div key={index}>
                  <ListItem
                    button
                    onClick={() => handleCategoryToggle(category.category)}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <ListItemText primary={category.category} />
                    <Badge color="primary" badgeContent={`${Math.round(progress)}%`} sx={{ mr: 2 }} />
                    {statusIcon}
                    {openCategories[category.category] ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={openCategories[category.category]} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 4, pr: 4, pt: 1 }}>
                      <LinearProgress variant="determinate" value={progress} sx={{ mb: 1 }} />
                      {Object.keys(category.levels).map((level) => (
                        <div key={level}>
                          <Typography variant="subtitle2" sx={{ pl: 2, color: 'text.secondary' }}>
                            {level.charAt(0).toUpperCase() + level.slice(1)} Issues:
                          </Typography>
                          <List component="div" disablePadding>
                            {category.levels[level].map((issue, idx) => {
                              const categoryKey = `${category.category}-${level}-${issue}`;
                              return (
                                <ListItem
                                  key={idx}
                                  sx={{ pl: 6 }}
                                  button
                                  onClick={() => handleToggle(category.category, level, issue)}
                                >
                                  <Checkbox
                                    checked={checkedItems[categoryKey] || false}
                                    tabIndex={-1}
                                    disableRipple
                                  />
                                  <ListItemText primary={issue} />
                                </ListItem>
                              );
                            })}
                          </List>
                        </div>
                      ))}
                    </Box>
                  </Collapse>
                </div>
              );
            })}
          </List>
        </Collapse>
      </Paper>
    );
  };
  
  export default InspectionChecklist;